import React, { useState } from 'react';

const DeveloperConsole = ({ 
  ws, 
  gameId, 
  devUnitSearch, 
  setDevUnitSearch,
  showGridOverlay,
  setShowGridOverlay,
  showDebugOverlay,
  setShowDebugOverlay,
  showTargetedDebug,
  setShowTargetedDebug,
  showElementSelector,
  setShowElementSelector,
  showEditableOverlay,
  setShowEditableOverlay,
  gridSize,
  setGridSize,
  targetElements,
  setTargetElements,
  handleElementSelected,
  handleElementMoved,
  handleElementResized,
  handleSaveDesign,
  savedDesign,
  setSavedDesign
}) => {
  const [isConsoleMinimized, setIsConsoleMinimized] = useState(false);
  const [forceHighlightEnemy, setForceHighlightEnemy] = useState(Boolean(typeof window !== 'undefined' && window.FORCE_HIGHLIGHT_ENEMY));

  return (
    <div 
      id="developer-console"
      style={{
        position: 'fixed',
        bottom: '20px',
        left: '20px',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
        padding: isConsoleMinimized ? '5px' : '10px',
        borderRadius: '8px',
        border: '2px solid #ff6b35',
        zIndex: 1000,
        minWidth: isConsoleMinimized ? '80px' : '300px',
        maxHeight: isConsoleMinimized ? '40px' : '400px',
        overflow: 'auto',
        cursor: 'move',
        userSelect: 'none',
        transition: 'all 0.3s ease'
      }}
      onMouseDown={(e) => {
        if (e.target.id === 'developer-console' || e.target.closest('#developer-console')) {
          const console = document.getElementById('developer-console');
          let isDragging = false;
          let startX = e.clientX - console.offsetLeft;
          let startY = e.clientY - console.offsetTop;

          const handleMouseMove = (e) => {
            if (isDragging) {
              console.style.left = (e.clientX - startX) + 'px';
              console.style.top = (e.clientY - startY) + 'px';
              console.style.bottom = 'auto';
            }
          };

          const handleMouseUp = () => {
            isDragging = false;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
          };

          isDragging = true;
          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        }
      }}
    >
      {/* Minimize/Maximize Button */}
      <div style={{ 
        position: 'absolute', 
        top: '5px', 
        right: '5px', 
        zIndex: 1001 
      }}>
        <button
          onClick={() => setIsConsoleMinimized(!isConsoleMinimized)}
          style={{
            backgroundColor: '#ff6b35',
            color: 'white',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '10px',
            fontWeight: 'bold'
          }}
        >
          {isConsoleMinimized ? 'Maximize' : 'Minimize'}
        </button>
      </div>

      {/* Console Content - Only show when not minimized */}
      {!isConsoleMinimized && (
        <>
          <div style={{ color: '#ff6b35', fontWeight: 'bold', marginBottom: '10px' }}>
            🛠️ Developer Console
          </div>
      
          {/* Enemy Zone Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Enemy Zones:</div>
            <button onClick={() => {
              const orderId = prompt('Enter order ID (default: 99):', '99');
              if (orderId && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Filling enemy flank with order ${orderId}`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devFillZone", 
                    zone: "flank",
                    orderId: parseInt(orderId),
                    targetPlayer: "opponent"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Fill Enemy Flank
            </button>
            <button onClick={() => {
              const orderId = prompt('Enter order ID (default: 99):', '99');
              if (orderId && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Filling enemy front with order ${orderId}`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devFillZone", 
                    zone: "front",
                    orderId: parseInt(orderId),
                    targetPlayer: "opponent"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Fill Enemy Front
            </button>
            <button onClick={() => {
              if (ws && ws.readyState === 1) {
                console.log('🛠️ DEV: Clear enemy flank');
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devClearZone", 
                    zone: "flank",
                    targetPlayer: "opponent"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Clear Enemy Flank
            </button>
            <button onClick={() => {
              if (ws && ws.readyState === 1) {
                console.log('🛠️ DEV: Clear enemy front');
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devClearZone", 
                    zone: "front",
                    targetPlayer: "opponent"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Clear Enemy Front
            </button>
          </div>

          {/* Player Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Player:</div>
            <button onClick={() => {
              if (ws && ws.readyState === 1) {
                console.log('🛠️ DEV: Clear player hand');
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devClearHand"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Clear Hand
            </button>
            <button onClick={() => {
              if (ws && ws.readyState === 1) {
                console.log('🛠️ DEV: Clear player front');
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devClearZone", 
                    zone: "front",
                    targetPlayer: "player"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Clear Front
            </button>
            <button onClick={() => {
              if (ws && ws.readyState === 1) {
                console.log('🛠️ DEV: Clear player flank');
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devClearZone", 
                    zone: "flank",
                    targetPlayer: "player"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Clear Flank
            </button>
          </div>

          {/* Drone Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Drone Deployment:</div>
            <button onClick={() => {
              const orderId = prompt('Enter drone ID (default: 18):', '18');
              if (orderId && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Deploy drone ${orderId} for opponent`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devDeployDrone", 
                    orderId: parseInt(orderId),
                    targetPlayer: "opponent"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Deploy Opponent Drone
            </button>
            <button onClick={() => {
              const orderId = prompt('Enter drone ID (default: 18):', '18');
              if (orderId && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Deploy drone ${orderId} for self`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devDeployDrone", 
                    orderId: parseInt(orderId),
                    targetPlayer: "self"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Deploy My Drone
            </button>
          </div>

        {/* Targeting Debug Controls */}
        <div style={{ marginBottom: '10px' }}>
          <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Targeting Debug:</div>
          <button
            onClick={() => {
              const next = !forceHighlightEnemy;
              setForceHighlightEnemy(next);
              try {
                if (typeof window !== 'undefined') {
                  window.FORCE_HIGHLIGHT_ENEMY = next;
                  // Notify app to re-render targeting
                  window.dispatchEvent(new Event('force-highlight-change'));
                }
              } catch (e) {}
              console.log(`🛠️ DEV: FORCE_HIGHLIGHT_ENEMY set to`, next);
            }}
            style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black', backgroundColor: forceHighlightEnemy ? '#ff0' : '#333' }}
          >
            {forceHighlightEnemy ? 'Disable Target Enemy Units' : 'Target Enemy Units'}
          </button>
        </div>

          {/* Player Stats Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Player Stats:</div>
            <button onClick={() => {
              const intelligence = prompt('Set current intelligence (default: 50):', '50');
              if (intelligence && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Set intelligence to ${intelligence}`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devSetIntelligence", 
                    intelligence: parseInt(intelligence)
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Set Intelligence
            </button>
            <button onClick={() => {
              const maxIntelligence = prompt('Set max intelligence (default: 5):', '5');
              if (maxIntelligence && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Set max intelligence to ${maxIntelligence}`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devSetMaxIntelligence", 
                    maxIntelligence: parseInt(maxIntelligence)
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Set Max Intelligence
            </button>
            <button onClick={() => {
              const health = prompt('Set player health (default: 30):', '30');
              if (health && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Set health to ${health}`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devSetHealth", 
                    health: parseInt(health)
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Set Health
            </button>
            <button onClick={() => {
              if (ws && ws.readyState === 1) {
                console.log('🛠️ DEV: Spawn unit 99 in enemy front and attack');
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devSpawnAndAttack", 
                    unitId: 99,
                    zone: "opponent_front"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Spawn Unit 99 & Attack
            </button>
          </div>

          {/* Unit Addition */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Add Unit to Hand:</div>
            <input 
              type="text" 
              placeholder="Search unit name or ID..."
              value={devUnitSearch}
              onChange={(e) => setDevUnitSearch(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '4px', 
                marginBottom: '5px',
                backgroundColor: '#333',
                color: 'white',
                border: '1px solid #555'
              }}
            />
            <button onClick={() => {
              if (devUnitSearch && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Add unit ${devUnitSearch} to hand`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devDeployUnit", 
                    unitId: parseInt(devUnitSearch) || devUnitSearch
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Add Selected Unit to Hand
            </button>
          </div>

          {/* Grid Overlay Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Layout Debugging:</div>
            <button onClick={() => setShowGridOverlay(!showGridOverlay)} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              {showGridOverlay ? 'Hide Grid' : 'Show Grid'}
            </button>
            <button onClick={() => setShowDebugOverlay(!showDebugOverlay)} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              {showDebugOverlay ? 'Hide All Boundaries' : 'Show All Boundaries'}
            </button>
            <button onClick={() => setShowTargetedDebug(!showTargetedDebug)} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              {showTargetedDebug ? 'Hide Targeted Boundaries' : 'Show Targeted Boundaries'}
            </button>
            <div style={{ marginTop: '5px' }}>
              <label style={{ color: '#ccc', fontSize: '10px' }}>Grid Size: </label>
              <input 
                type="number" 
                value={gridSize} 
                onChange={(e) => setGridSize(parseInt(e.target.value) || 20)}
                style={{ width: '50px', padding: '2px', backgroundColor: '#333', color: 'white', border: '1px solid #555' }}
                min="10"
                max="100"
              />
            </div>
          </div>

          {/* State Sync Monitor Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>🚦 State Sync Monitor:</div>
            <button 
              onClick={() => {
                const isEnabled = localStorage.getItem('enableStateSyncMonitor') === 'true';
                if (isEnabled) {
                  localStorage.removeItem('enableStateSyncMonitor');
                  console.log('🛠️ DEV: State Sync Monitor disabled');
                } else {
                  localStorage.setItem('enableStateSyncMonitor', 'true');
                  console.log('🛠️ DEV: State Sync Monitor enabled');
                }
                // Force page reload to apply changes
                window.location.reload();
              }}
              style={{ 
                margin: '2px', 
                padding: '4px 8px', 
                fontSize: '12px', 
                backgroundColor: localStorage.getItem('enableStateSyncMonitor') === 'true' ? '#4CAF50' : '#F44336',
                color: 'white',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {localStorage.getItem('enableStateSyncMonitor') === 'true' ? '🟢 Monitor ON' : '🔴 Monitor OFF'}
            </button>
            <div style={{ fontSize: '10px', color: '#ccc', marginTop: '5px' }}>
              Monitors frontend/backend state synchronization
            </div>
          </div>

          {/* Targeted Element Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Target Specific Elements:</div>
            <button onClick={() => setShowElementSelector(!showElementSelector)} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              {showElementSelector ? 'Hide Element Selector' : 'Show Element Selector'}
            </button>
            <button onClick={() => setTargetElements([])} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Clear All
            </button>
          </div>

          {/* Editable Design Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>🎨 Design Mode:</div>
            <button onClick={() => setShowEditableOverlay(!showEditableOverlay)} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black', backgroundColor: showEditableOverlay ? '#ff00ff' : '#333' }}>
              {showEditableOverlay ? 'Exit Design Mode' : 'Enter Design Mode'}
            </button>
            <div style={{ fontSize: '10px', color: '#ccc', marginTop: '5px' }}>
              {showEditableOverlay ? 'Drag elements to move, resize from corner' : 'Enable to drag & resize UI elements'}
            </div>
            <div style={{ color: '#ccc', fontSize: '10px', marginTop: '5px' }}>
              Targeted: {targetElements.length} elements
            </div>
            <button
              onClick={() => {
                localStorage.removeItem('battleUI_design');
                setSavedDesign({});
                alert('Design changes cleared! Page will reload to reset elements.');
                window.location.reload();
              }}
              style={{
                backgroundColor: '#ff4444',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '10px',
                fontWeight: 'bold',
                marginTop: '5px'
              }}
            >
              Clear Saved Design
            </button>
          </div>

          <div style={{ fontSize: '10px', color: '#888', marginTop: '10px' }}>
            Developer Mode Active
          </div>
        </>
      )}
    </div>
  );
};

export default DeveloperConsole;
